import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useNotification, useParsed } from "@refinedev/core";

import { prefillDatetimeForPicker } from "@/lib/dateUtils";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import {
  ArtistNestedType,
  artistNestedSchema,
} from "@/pages/artists/schemas/artist.nested.form";
import { useEventCreateFormStore } from "@/pages/events/create/hooks/use-event-create-form";
import type { EventBasicInfoType } from "@/pages/events/schemas/event.create.form";
import { useGenreRepository } from "@/pages/genres/hooks/use-genre-repository";
import {
  GenreNestedType,
  genreNestedSchema,
} from "@/pages/genres/schemas/genre.nested.form";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import { venueNestedSchema } from "@/pages/venues/schemas/venue.nested.form";

import {
  clipboardDataSchema,
  readClipboardGenreRefs,
  readClipboardLineupRefs,
  readClipboardVenueRef,
  readEventMetadata,
  readTinaHitlSource,
} from "./clipboard-data";
import type {
  ClipboardData,
  ClipboardEntityRef,
  ClipboardNamedEntityRef,
} from "./clipboard-data";

type ClipboardFormData = Partial<EventBasicInfoType>;

function stringSimilarity(str1: string, str2: string): number {
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/^(the|dj|mc)\s+/i, "")
      .replace(/\s*[&+]\s*/g, " and ")
      .replace(/['']/g, "'")
      .trim();

  const s1 = normalize(str1);
  const s2 = normalize(str2);

  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;

  const bigrams1 = new Set<string>();
  const bigrams2 = new Set<string>();

  for (let i = 0; i < s1.length - 1; i++) bigrams1.add(s1.slice(i, i + 2));
  for (let i = 0; i < s2.length - 1; i++) bigrams2.add(s2.slice(i, i + 2));

  const intersection = [...bigrams1].filter((b) => bigrams2.has(b)).length;
  return (2 * intersection) / (bigrams1.size + bigrams2.size);
}

export function useDataFromClipboard() {
  const { action } = useParsed();
  const [searchParams] = useSearchParams();
  const { search } = useGeodataRepository();
  const { search: searchGenres } = useGenreRepository();
  const { get: getVenue, search: searchVenues } = useVenueRepository();
  const { get: getArtist, search: searchArtists } = useArtistRepository();
  const { open } = useNotification();

  const [data, setData] = useState<ClipboardFormData>();
  const updateTinaHitlSource = useEventCreateFormStore(
    (state) => state.updateTinaHitlSource,
  );
  const handlingClipboard = useRef(false);

  useEffect(() => {
    if (action !== "create") return;
    const populate = searchParams.get("populate");
    // `cliboard` is an intentional typo alias — see
    // docs/superpowers/plans/2026-05-14-tina-hitl-ui-event-prefill.md.
    if (populate === "clipboard" || populate === "cliboard") {
      handleClipboard();
    }
  }, [searchParams, action]);

  async function validateLineup(
    smellsArtists: ClipboardNamedEntityRef[],
  ): Promise<ArtistNestedType[]> {
    const resolved = await Promise.all(
      smellsArtists.map((smellsArtist) => resolveArtistRef(smellsArtist)),
    );

    const artists: ArtistNestedType[] = [];
    const seenArtistIds = new Set<string>();
    const seenArtistNames = new Set<string>();

    for (const artist of resolved) {
      if (!artist) continue;
      if (artist.id && seenArtistIds.has(artist.id)) continue;
      if (seenArtistNames.has(artist.name.toLowerCase())) continue;
      rememberArtist(artist, seenArtistIds, seenArtistNames);
      artists.push(artist);
    }

    return artists;
  }

  async function resolveArtistRef(
    smellsArtist: ClipboardNamedEntityRef,
  ): Promise<ArtistNestedType | null> {
    const fallbackName = smellsArtist.name?.trim() ?? "";

    if (smellsArtist.id) {
      try {
        const matchedById = await getArtist(smellsArtist.id);
        return parseArtistNested(matchedById);
      } catch (error) {
        open?.({
          type: "error",
          message: `Artist id "${smellsArtist.id}" was not found.${
            fallbackName ? " Falling back to name search." : ""
          }`,
        });
      }
    }

    if (!fallbackName) return null;

    const response = await searchArtists(fallbackName);
    if (response.length === 0) {
      open?.({
        type: "error",
        message: `Artist "${fallbackName}" not found. It will be created with just the name.`,
      });
      return artistNestedSchema.parse({ name: fallbackName });
    }

    const matchedArtist = response[0];
    const similarity = stringSimilarity(fallbackName, matchedArtist.name ?? "");

    if (similarity < 0.9) {
      return artistNestedSchema.parse({ name: fallbackName });
    }

    return parseArtistNested(matchedArtist);
  }

  async function validateGenres(
    smellsGenres: ClipboardNamedEntityRef[],
  ): Promise<GenreNestedType[]> {
    const genres: GenreNestedType[] = [];
    const seenGenres = new Set<string>();

    for (const smellsGenre of smellsGenres) {
      const genreName = smellsGenre.name?.trim().toLowerCase() ?? "";
      const dedupeKey = smellsGenre.id ?? genreName;
      if (!dedupeKey || seenGenres.has(dedupeKey)) continue;
      seenGenres.add(dedupeKey);

      if (smellsGenre.id && smellsGenre.name) {
        genres.push(genreNestedSchema.parse(smellsGenre));
        continue;
      }

      if (!genreName) continue;
      const response = await searchGenres(genreName);
      if (response.length === 0) {
        continue;
      }
      genres.push(response[0]);
    }

    return genres;
  }

  async function validateVenue(smellsVenue: ClipboardEntityRef | undefined) {
    if (!smellsVenue) return undefined;

    if (smellsVenue.id) {
      try {
        return venueNestedSchema.parse(await getVenue(smellsVenue.id));
      } catch (error) {
        open?.({
          type: "error",
          message: `Venue id "${smellsVenue.id}" was not found. Falling back to name search.`,
        });
      }
    }

    if (!smellsVenue.name) return undefined;

    try {
      const response = await searchVenues(smellsVenue.name);
      const venue = venueNestedSchema.parse(response?.[0]);
      return venue;
    } catch (error) {}
  }

  async function validateCity(smellsCity: string | undefined) {
    if (!smellsCity) {
      return undefined;
    }
    try {
      const response = await search(smellsCity);
      if (!response || response.length === 0) {
        return undefined;
      }
      return response[0];
    } catch (error) {
      console.error("Error validating city:", error);
      return undefined;
    }
  }

  async function handleClipboard() {
    if (handlingClipboard.current) {
      console.warn("Already handling clipboard data, skipping...");
      return;
    }

    handlingClipboard.current = true;
    try {
      const clipboardData = await navigator.clipboard.readText();
      const json = JSON.parse(clipboardData);
      const parsed = clipboardDataSchema.parse(json);
      const nextData = await buildFormData(parsed);

      setData(nextData);
      updateTinaHitlSource(readTinaHitlSource(parsed));
    } catch (error) {
      open?.({
        type: "error",
        message: "Clipboard data could not be loaded.",
        description: toHumanErrorMessage(error),
      });
    } finally {
      handlingClipboard.current = false;
    }
  }

  async function buildFormData(
    parsed: ClipboardData,
  ): Promise<ClipboardFormData> {
    const [lineup, venue, city] = await Promise.all([
      validateLineup(readClipboardLineupRefs(parsed)),
      validateVenue(readClipboardVenueRef(parsed)),
      validateCity(parsed.city),
    ]);
    const lineupGenreRefs = lineup
      .map((item) => item.genres ?? [])
      .flat()
      .map((genre) => ({ id: genre.id, name: genre.name.toLowerCase() }));
    const allGenreRefs = dedupeGenreRefs([
      ...readClipboardGenreRefs(parsed),
      ...lineupGenreRefs,
    ]);
    const genres = await validateGenres(allGenreRefs);
    const metadata = readEventMetadata(parsed);

    return {
      name: parsed.name ?? "",
      type: (parsed.type ?? "Event") as EventBasicInfoType["type"],
      about: parsed.about,
      venue: venue,
      geodata: city,
      lineup: lineup,
      startDate: prefillDatetimeForPicker(parsed.startDate, parsed.timezone),
      endDate: prefillDatetimeForPicker(parsed.endDate, parsed.timezone),
      genres: genres,
      geolocation: {
        latitude: venue?.geolocation?.latitude,
        longitude: venue?.geolocation?.longitude,
      },
      timezone: parsed.timezone,
      ticketUrl: parsed.ticketingUrl,
      metadata: metadata?.id
        ? {
            id: metadata.id,
            url: metadata.url,
            site: metadata.site,
            jobId: metadata.jobId,
            timestamp: metadata.timestamp
              ? new Date(metadata.timestamp).toISOString()
              : undefined,
            source: metadata.source,
          }
        : undefined,
    };
  }

  function dedupeGenreRefs(
    genreRefs: ClipboardNamedEntityRef[],
  ): ClipboardNamedEntityRef[] {
    const seenGenres = new Set<string>();
    return genreRefs.filter((genreRef) => {
      const key = genreRef.id ?? genreRef.name?.trim().toLowerCase();
      if (!key || seenGenres.has(key)) return false;
      seenGenres.add(key);
      return true;
    });
  }

  function parseArtistNested(artist: unknown): ArtistNestedType {
    const artistWithPhoto = artist as { media?: { photos?: unknown[] } };
    return artistNestedSchema.parse({
      ...artistWithPhoto,
      photo: artistWithPhoto.media?.photos?.[0],
    });
  }

  function rememberArtist(
    artist: ArtistNestedType,
    seenArtistIds: Set<string>,
    seenArtistNames: Set<string>,
  ) {
    if (artist.id) seenArtistIds.add(artist.id);
    seenArtistNames.add(artist.name.toLowerCase());
  }

  return { data };
}
